import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type PatientRelationRow =
  Database['public']['Functions']['list_patient_relations']['Returns'][number];

export const patientRelationsQueryKey = (patientId: string | null) =>
  ['patient-relations', patientId] as const;

export function usePatientRelations(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientRelationsQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientRelationRow[]> => {
      if (!patientId) return [];
      const { data, error } = await supabase.rpc('list_patient_relations', {
        p_patient_id: patientId,
      });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export interface CreatePatientRelationInput {
  from_patient_id: string;
  relation_type: string;
  to_patient_id?: string | null;
  to_contact_id?: string | null;
  notes?: string | null;
}

export function useCreatePatientRelation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePatientRelationInput) => {
      const { data, error } = await supabase.rpc('create_patient_relation', {
        p_from_patient_id: input.from_patient_id,
        p_relation_type: input.relation_type,
        p_to_patient_id: input.to_patient_id ?? null,
        p_to_contact_id: input.to_contact_id ?? null,
        p_notes: input.notes ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_row, input) => {
      queryClient.invalidateQueries({
        queryKey: patientRelationsQueryKey(input.from_patient_id),
      });
      if (input.to_patient_id) {
        queryClient.invalidateQueries({
          queryKey: patientRelationsQueryKey(input.to_patient_id),
        });
      }
    },
  });
}

export function useDeletePatientRelation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { relation_id: string; patient_id: string; related_patient_id?: string | null }) => {
      const { error } = await supabase.rpc('delete_patient_relation', {
        p_relation_id: input.relation_id,
      });
      if (error) throw error;
    },
    onSuccess: (_void, input) => {
      queryClient.invalidateQueries({
        queryKey: patientRelationsQueryKey(input.patient_id),
      });
      if (input.related_patient_id) {
        queryClient.invalidateQueries({
          queryKey: patientRelationsQueryKey(input.related_patient_id),
        });
      }
    },
  });
}
